/**
 * WizardStep — renders the body of each guided wizard step.
 *
 * It maps a ChatReply to the appropriate input UI:
 *   kind:'ask' + slot → big headline + the right structured input
 *   kind:'close'      → recommended spaces (final reveal) + success summary
 *
 * Value shapes sent upstream (must match orchestrator's applyWidgetAnswer):
 *   businessType  → string (category slug)
 *   city          → { slug, name, placeId, lat, lng, countryCode }
 *   neighborhood  → { slug, name, placeId, lat, lng, countryCode }
 *   budgetAmount  → number
 *   budgetPeriod  → 'monthly'|'daily'|'hourly'
 *   headcount     → number
 *   timing        → 'now'|'lt_3mo'|'exploring'
 *   contact       → { name, email, phone? }
 */
import { useEffect, useRef, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useCategories } from '~/utils/hooks/useCategories';
import type { ChatReply, ChatCard } from '~/.server/chat/orchestrator.server';
import type { SlotName } from '~/.server/chat/slots';
import { cn } from '~/lib/utils';
import { getNeighborhoodsForCity } from './neighborhoodData';
import { urlPattern } from '~/utils/urlPattern';
import { slugify } from './slugify';

// ── Geo value shape (city / neighborhood) ──────────────────────────────────

type GeoValue = {
  slug: string;
  name: string;
  placeId: string;
  lat: string;
  lng: string;
  countryCode: string;
};

// ── Props ──────────────────────────────────────────────────────────────────

export type BudgetCompoundValue = {
  budgetAmount: number;
  budgetPeriod: 'monthly' | 'daily' | 'hourly';
};

export type SlotValue =
  | string
  | number
  | GeoValue
  | BudgetCompoundValue
  | 'now' | 'lt_3mo' | 'exploring'
  | { name: string; email: string; phone?: string; termsAccepted: boolean };

type WizardStepProps = {
  reply: ChatReply;
  /** Pending value for the current step (controls CTA disabled state) */
  pending: SlotValue | null;
  onValue: (slot: SlotName, value: SlotValue) => void;
  /** Called when user clicks "Enviármelos por email" on cards step */
  onRequestEmail: () => void;
  /** Current slot state summary for the close card */
  stateSummary?: string;
  contactEmail?: string;
  /** City slug from SlotState (e.g. 'madrid') — drives neighborhood chips */
  citySlug?: string;
  /** City geometry captured at the city step — biases neighborhood autocomplete */
  cityLat?: string;
  cityLng?: string;
};

// ── Shared option card ────────────────────────────────────────────────────

function OptionCard({
  selected,
  onClick,
  icon,
  label,
  hint,
  small = false,
}: {
  selected: boolean;
  onClick: () => void;
  icon?: string;
  label: string;
  hint?: string;
  small?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-xl border-2 text-left transition-colors',
        small
          ? 'justify-center p-3 text-sm font-semibold'
          : 'px-4 py-3 text-sm font-semibold',
        selected
          ? 'border-neutral-900 bg-neutral-900 text-white'
          : 'border-neutral-200 bg-white text-neutral-900 hover:border-neutral-400',
      )}
    >
      {!small && icon && (
        <span
          className={cn(
            'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-base',
            selected ? 'bg-white/20' : 'bg-neutral-100',
          )}
        >
          {icon}
        </span>
      )}
      <span>
        {label}
        {hint && (
          <span className={cn('block text-xs font-normal', selected ? 'text-white/70' : 'text-neutral-500')}>
            {hint}
          </span>
        )}
      </span>
    </button>
  );
}

// ── Google Places autocomplete (standalone, no Conform dependency) ─────────

type PlaceResult = {
  slug: string;
  name: string;
  placeId: string;
  lat: string;
  lng: string;
  countryCode: string;
};

function PlaceInput({
  placeholder,
  bias,
  onSelect,
  className,
}: {
  placeholder: string;
  bias?: google.maps.LatLngBounds | null;
  onSelect: (r: PlaceResult) => void;
  className?: string;
}) {
  const places = useMapsLibrary('places');
  const inputRef = useRef<HTMLInputElement>(null);
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);

  // Keep the latest `onSelect` in a ref so the Autocomplete effect can stay
  // dependency-free. `onSelect` is typically a fresh closure on every parent
  // render; including it in the effect deps would tear down and recreate the
  // Autocomplete on every keystroke, so predictions never stabilize.
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  });

  // Create the Autocomplete ONCE, when the places library is ready. Never
  // recreate it on re-render (that was the empty-dropdown bug).
  useEffect(() => {
    if (!places || !inputRef.current) return;
    const ac = new places.Autocomplete(inputRef.current, {
      fields: ['address_components', 'geometry', 'name', 'place_id'],
    });
    acRef.current = ac;
    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      const comp = (type: string) =>
        place.address_components?.find((c) => c.types.includes(type));
      const city =
        comp('locality')?.long_name ??
        comp('postal_town')?.long_name ??
        comp('administrative_area_level_2')?.long_name ??
        '';
      const neighborhood =
        comp('neighborhood')?.long_name ??
        comp('sublocality')?.long_name ??
        comp('sublocality_level_1')?.long_name ??
        '';
      const countryCode = comp('country')?.short_name ?? 'ES';
      const name = neighborhood || city || place.name || '';
      onSelectRef.current({
        slug: slugify(name),
        name,
        placeId: place.place_id ?? '',
        lat: place.geometry?.location?.lat()?.toString() ?? '',
        lng: place.geometry?.location?.lng()?.toString() ?? '',
        countryCode,
      });
    });
    return () => {
      google.maps.event.removeListener(listener);
      acRef.current = null;
    };
  }, [places]);

  // When the city-bias bounds change, update the existing Autocomplete in place
  // rather than recreating it.
  useEffect(() => {
    if (!acRef.current) return;
    acRef.current.setBounds(bias ?? undefined);
  }, [bias]);

  return (
    <div className={cn('flex items-center gap-2 rounded-xl border-2 border-neutral-200 bg-white px-4 py-3', className)}>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="flex-shrink-0 text-neutral-400"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4-4" />
      </svg>
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        className="flex-1 bg-transparent text-sm text-neutral-900 outline-none placeholder:text-neutral-400"
      />
    </div>
  );
}

// ── Slot: businessType ────────────────────────────────────────────────────

function BusinessTypeStep({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string) => void;
}) {
  const { data: categories = [] } = useCategories();

  return (
    <div className="flex flex-col gap-2.5">
      {categories.map((cat) => (
        <OptionCard
          key={cat.value}
          selected={value === cat.value}
          onClick={() => onChange(cat.value)}
          label={cat.label}
        />
      ))}
    </div>
  );
}

// ── Slot: city ────────────────────────────────────────────────────────────

function CityStep({
  value,
  onChange,
}: {
  value: GeoValue | null;
  onChange: (v: GeoValue) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <PlaceInput
        placeholder="Busca tu ciudad…"
        onSelect={(r) => onChange({ ...r })}
      />
      {value && (
        <div className="rounded-xl border-2 border-neutral-900 bg-neutral-50 px-4 py-3">
          <p className="text-sm font-semibold text-neutral-900">{value.name}</p>
          <p className="text-xs text-neutral-500">Ciudad confirmada</p>
        </div>
      )}
    </div>
  );
}

// ── Slot: neighborhood ────────────────────────────────────────────────────

function NeighborhoodStep({
  value,
  citySlug,
  cityName,
  cityLat,
  cityLng,
  onChange,
}: {
  value: GeoValue | null;
  citySlug?: string;
  cityName?: string;
  cityLat?: string;
  cityLng?: string;
  onChange: (v: GeoValue) => void;
}) {
  const places = useMapsLibrary('places');
  const chips = citySlug ? getNeighborhoodsForCity(citySlug) : [];

  // Build a ~20km bias box around the city center (if its geometry was captured
  // at the city step) so neighborhood predictions favor the selected city.
  const [bias, setBias] = useState<google.maps.LatLngBounds | null>(null);
  useEffect(() => {
    if (!places) return;
    const lat = cityLat ? Number(cityLat) : NaN;
    const lng = cityLng ? Number(cityLng) : NaN;
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      setBias(null);
      return;
    }
    const delta = 0.18; // ~20km
    setBias(
      new google.maps.LatLngBounds(
        { lat: lat - delta, lng: lng - delta },
        { lat: lat + delta, lng: lng + delta },
      ),
    );
  }, [places, cityLat, cityLng]);

  return (
    <div className="flex flex-col gap-3">
      {chips.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {chips.map((n) => (
            <OptionCard
              key={n.slug}
              selected={value?.slug === n.slug}
              small
              label={n.label}
              onClick={() =>
                onChange({
                  slug: n.slug,
                  name: n.label,
                  placeId: '',
                  lat: '',
                  lng: '',
                  countryCode: 'ES',
                })
              }
            />
          ))}
        </div>
      )}
      <PlaceInput
        placeholder={cityName ? `Buscar barrio en ${cityName}…` : 'Buscar otro barrio…'}
        bias={bias}
        onSelect={(r) =>
          onChange({
            slug: r.slug,
            name: r.name,
            placeId: r.placeId,
            lat: r.lat,
            lng: r.lng,
            countryCode: r.countryCode,
          })
        }
      />
      {value && (
        <div className="rounded-xl border-2 border-neutral-900 bg-neutral-50 px-4 py-3">
          <p className="text-sm font-semibold text-neutral-900">{value.name}</p>
          <p className="text-xs text-neutral-500">Zona seleccionada</p>
        </div>
      )}
    </div>
  );
}

// ── Slot: budgetAmount + budgetPeriod ─────────────────────────────────────

const PERIODS = [
  { value: 'monthly', label: 'mes' },
  { value: 'daily', label: 'día' },
  { value: 'hourly', label: 'hora' },
] as const;

type BudgetValue = { amount: number | null; period: 'monthly' | 'daily' | 'hourly' };

function BudgetStep({
  value,
  onChange,
}: {
  value: BudgetValue;
  onChange: (v: BudgetValue) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 rounded-xl border-2 border-neutral-200 bg-white px-4 py-3">
        <span className="text-sm text-neutral-400">€</span>
        <input
          type="number"
          min="0"
          placeholder="Presupuesto"
          className="flex-1 bg-transparent text-sm text-neutral-900 outline-none placeholder:text-neutral-400"
          value={value.amount ?? ''}
          onChange={(e) =>
            onChange({ ...value, amount: e.target.value ? Number(e.target.value) : null })
          }
        />
        <span className="text-sm text-neutral-400">/</span>
        <select
          className="bg-transparent text-sm text-neutral-900 outline-none"
          value={value.period}
          onChange={(e) =>
            onChange({ ...value, period: e.target.value as BudgetValue['period'] })
          }
        >
          {PERIODS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ── Slot: headcount ───────────────────────────────────────────────────────

function HeadcountStep({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded-xl border-2 border-neutral-200 bg-white px-4 py-3">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="flex-shrink-0 text-neutral-400"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
      <input
        type="number"
        min="1"
        placeholder="Número de personas"
        className="flex-1 bg-transparent text-sm text-neutral-900 outline-none placeholder:text-neutral-400"
        value={value ?? ''}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (!isNaN(n) && n > 0) onChange(n);
        }}
      />
    </div>
  );
}

// ── Slot: timing ──────────────────────────────────────────────────────────

const TIMING_OPTIONS = [
  { value: 'now' as const, label: 'Lo necesito ahora', hint: 'Urgente' },
  { value: 'lt_3mo' as const, label: 'En menos de 3 meses', hint: 'Planificando' },
  { value: 'exploring' as const, label: 'Estoy explorando', hint: 'Sin prisa' },
];

function TimingStep({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: 'now' | 'lt_3mo' | 'exploring') => void;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      {TIMING_OPTIONS.map((o) => (
        <OptionCard
          key={o.value}
          selected={value === o.value}
          label={o.label}
          hint={o.hint}
          onClick={() => onChange(o.value)}
        />
      ))}
    </div>
  );
}

// ── Slot: contact ─────────────────────────────────────────────────────────

type ContactValue = { name: string; email: string; phone: string; termsAccepted: boolean };

function ContactStep({
  value,
  onChange,
}: {
  value: ContactValue;
  onChange: (v: ContactValue) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <input
        type="text"
        placeholder="Tu nombre"
        className="w-full rounded-xl border-2 border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-neutral-900"
        value={value.name}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
      />
      <input
        type="email"
        placeholder="Tu email"
        className="w-full rounded-xl border-2 border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-neutral-900"
        value={value.email}
        onChange={(e) => onChange({ ...value, email: e.target.value })}
      />
      <input
        type="tel"
        placeholder="Teléfono (opcional)"
        className="w-full rounded-xl border-2 border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-neutral-900"
        value={value.phone}
        onChange={(e) => onChange({ ...value, phone: e.target.value })}
      />
      <label className="flex items-start gap-2.5 text-xs text-neutral-600">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900"
          checked={value.termsAccepted}
          onChange={(e) => onChange({ ...value, termsAccepted: e.target.checked })}
        />
        <span>
          He leído y acepto los{' '}
          <a
            href="/politica-de-privacidad"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-neutral-900"
          >
            términos y condiciones
          </a>
          .
        </span>
      </label>
    </div>
  );
}

// ── Recommendation cards ──────────────────────────────────────────────────

export function RecommendationCard({ card }: { card: ChatCard }) {
  let url = '#';
  if (card.citySlug && card.category && card.slug) {
    url = urlPattern.build({ city: card.citySlug, category: card.category, space: card.slug });
  } else if (card.citySlug && card.slug) {
    url = `/espacios/${card.citySlug}`;
  }
  const imageUrl = typeof card.image === 'string' ? card.image : null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 rounded-xl border-2 border-neutral-200 bg-white p-3 transition-colors hover:border-neutral-400 no-underline"
    >
      <div className="h-14 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-neutral-100">
        {imageUrl ? (
          <img src={imageUrl} alt={card.title} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-blue-100 to-blue-200" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-neutral-900">{card.title}</p>
        {(card.city || card.category) && (
          <p className="truncate text-xs text-neutral-500">
            {[card.category, card.city].filter(Boolean).join(' · ')}
          </p>
        )}
        {card.priceMin != null && (
          <p className="text-sm font-extrabold text-neutral-900">
            {card.priceMin.toLocaleString('es-ES')} {card.currency}/mes
          </p>
        )}
      </div>
    </a>
  );
}

// ── Step headline map ──────────────────────────────────────────────────────

export const SLOT_HEADLINES: Record<SlotName, { q: string; hint?: string; optional?: boolean }> = {
  businessType: {
    q: '¿Qué tipo de espacio buscas?',
    hint: 'Elige la categoría que mejor describa tu negocio.',
  },
  city: {
    q: '¿En qué ciudad buscas?',
    hint: 'Te mostraremos espacios disponibles en esa ciudad.',
  },
  neighborhood: {
    q: '¿En qué zona de la ciudad?',
    hint: 'Elige una zona popular o búscala.',
  },
  budgetAmount: {
    q: '¿Cuál es tu presupuesto?',
    hint: 'Aproximado está bien.',
  },
  budgetPeriod: {
    q: '¿Cuál es tu presupuesto?',
    hint: 'Aproximado está bien.',
  },
  headcount: {
    q: '¿Cuántas personas lo usarán?',
    hint: 'Número habitual de personas.',
  },
  timing: {
    q: '¿Cuándo necesitas el espacio?',
  },
  contact: {
    q: 'Casi listo — ¿cómo te enviamos los resultados?',
    hint: 'Solo te contactaremos con espacios que encajen.',
  },
};

// ── Main export ────────────────────────────────────────────────────────────

export type { GeoValue };
export type { WizardStepProps };

/**
 * SlotInputImpl — renders ONLY the active slot's input control (no headline).
 * Owns the internal budget/contact state that the input sub-components need.
 * Used by both WizardStep (which adds a headline above it) and SlotInput
 * (which exposes it standalone for in-thread rendering).
 */
function SlotInputImpl({
  reply,
  pending,
  onValue,
  citySlug,
  cityLat,
  cityLng,
}: WizardStepProps) {
  // Budget step needs internal state to track both amount and period together
  const [budget, setBudget] = useState<{ amount: number | null; period: 'monthly' | 'daily' | 'hourly' }>({
    amount: null,
    period: 'monthly',
  });
  const [contact, setContact] = useState<ContactValue>({ name: '', email: '', phone: '', termsAccepted: false });

  if (reply.kind !== 'ask') return null;
  const slot = reply.slot;

  switch (slot) {
    case 'businessType':
      return (
        <BusinessTypeStep
          value={typeof pending === 'string' ? pending : null}
          onChange={(v) => onValue('businessType', v)}
        />
      );

    case 'city':
      return (
        <CityStep
          value={pending && typeof pending === 'object' && 'slug' in (pending as object) ? (pending as GeoValue) : null}
          onChange={(v) => onValue('city', v)}
        />
      );

    case 'neighborhood': {
      const geoVal = pending && typeof pending === 'object' && 'slug' in (pending as object) ? (pending as GeoValue) : null;
      return (
        <NeighborhoodStep
          value={geoVal}
          citySlug={citySlug}
          cityName={citySlug}
          cityLat={cityLat}
          cityLng={cityLng}
          onChange={(v) => onValue('neighborhood', v)}
        />
      );
    }

    case 'budgetAmount':
    case 'budgetPeriod':
      return (
        <BudgetStep
          value={budget}
          onChange={(v) => {
            setBudget(v);
            if (v.amount != null) {
              onValue('budgetAmount', { budgetAmount: v.amount, budgetPeriod: v.period });
            }
          }}
        />
      );

    case 'headcount':
      return (
        <HeadcountStep
          value={typeof pending === 'number' ? pending : null}
          onChange={(v) => onValue('headcount', v)}
        />
      );

    case 'timing':
      return (
        <TimingStep
          value={typeof pending === 'string' ? pending : null}
          onChange={(v) => onValue('timing', v)}
        />
      );

    case 'contact':
      return (
        <ContactStep
          value={contact}
          onChange={(v) => {
            setContact(v);
            // Only emit a non-null pending value (which enables "Enviar") once
            // the email is present AND the T&C checkbox is checked.
            const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email);
            if (emailValid && v.termsAccepted) {
              onValue('contact', {
                name: v.name,
                email: v.email,
                phone: v.phone || undefined,
                termsAccepted: true,
              });
            } else {
              onValue('contact', null as unknown as SlotValue);
            }
          }}
        />
      );

    default:
      return null;
  }
}

/** Renders ONLY the active slot's input control (no headline) for in-thread use. */
export function SlotInput(props: WizardStepProps) {
  return <SlotInputImpl {...props} />;
}

export function WizardStep({
  reply,
  pending,
  onValue,
  onRequestEmail,
  stateSummary,
  contactEmail,
  citySlug,
  cityLat,
  cityLng,
}: WizardStepProps) {
  if (reply.kind === 'close') {
    const cards = reply.cards ?? [];
    return (
      <div className="flex flex-col gap-4 py-2">
        {/* Recommended spaces — the final reveal, shown above the email confirmation */}
        {cards.length > 0 ? (
          <div className="flex flex-col gap-2.5">
            <p className="text-lg font-bold leading-tight tracking-tight text-neutral-900">
              {`${cards.length} espacio${cards.length !== 1 ? 's' : ''} que encajan`}
            </p>
            {cards.map((card) => (
              <RecommendationCard key={card.id} card={card} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <p className="text-lg font-bold leading-tight tracking-tight text-neutral-900">
              Aún no hay coincidencias exactas
            </p>
            <p className="text-sm text-neutral-500">
              Seguiremos buscando y te avisaremos cuando aparezcan espacios nuevos.
            </p>
          </div>
        )}

        {/* Email confirmation */}
        <div className="flex items-center gap-3 rounded-xl border-2 border-green-200 bg-green-50 px-4 py-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          <p className="text-sm text-neutral-700">{reply.text}</p>
        </div>

        {stateSummary && (
          <div className="w-full rounded-xl border-2 border-neutral-200 bg-neutral-50 px-4 py-3 text-left">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400">Tu búsqueda</p>
            <p className="mt-1 text-sm text-neutral-700">{stateSummary}</p>
          </div>
        )}
      </div>
    );
  }

  // kind === 'ask'
  const slot = reply.slot;
  const meta = SLOT_HEADLINES[slot] ?? { q: slot };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-lg font-bold leading-tight tracking-tight text-neutral-900">{meta.q}</p>
        {meta.hint && <p className="mt-1 text-xs text-neutral-500">{meta.hint}</p>}
      </div>
      <SlotInputImpl
        reply={reply}
        pending={pending}
        onValue={onValue}
        onRequestEmail={onRequestEmail}
        stateSummary={stateSummary}
        contactEmail={contactEmail}
        citySlug={citySlug}
        cityLat={cityLat}
        cityLng={cityLng}
      />
    </div>
  );
}
